import React, { useState, useEffect, useCallback } from 'react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid, Legend } from 'recharts';
import api from '../services/api';

const COLORS = {
  revenueRecovered: '#059669',
  paymentsCollected: '#2563EB',
  revenueProtected: '#7C3AED',
  staffTimeSaved: '#CA8A04',
};

function ROIDashboard({ practiceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(1);
  const [subscriptionCost, setSubscriptionCost] = useState(549);
  const [staffHourlyRate, setStaffHourlyRate] = useState(25);

  // Load saved values from localStorage once practiceId is available
  useEffect(() => {
    if (!practiceId) return;
    const savedCost = localStorage.getItem(`roi_subscription_cost_${practiceId}`);
    const savedRate = localStorage.getItem(`roi_staff_rate_${practiceId}`);
    if (savedCost) setSubscriptionCost(Number(savedCost));
    if (savedRate) setStaffHourlyRate(Number(savedRate));
  }, [practiceId]);

  // Persist to localStorage on change (only when practiceId is valid)
  useEffect(() => {
    if (practiceId) localStorage.setItem(`roi_subscription_cost_${practiceId}`, subscriptionCost);
  }, [subscriptionCost, practiceId]);

  useEffect(() => {
    if (practiceId) localStorage.setItem(`roi_staff_rate_${practiceId}`, staffHourlyRate);
  }, [staffHourlyRate, practiceId]);

  const loadData = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const result = await api.get(
        `/practices/${practiceId}/roi/dashboard?months=${months}&subscriptionCost=${subscriptionCost}&staffHourlyRate=${staffHourlyRate}`
      );
      if (result.success) setData(result.data);
    } catch (err) {
      console.error('Error loading ROI data:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, months, subscriptionCost, staffHourlyRate]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><p>Loading ROI Dashboard...</p></div>;
  }

  if (!data) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><p>No data available</p></div>;
  }

  const roiMultiplier = subscriptionCost > 0
    ? Math.round((data.totalValue / subscriptionCost) * 10) / 10
    : 0;

  const breakdownData = [
    { name: 'Revenue Recovered', value: data.revenueRecovered.total, fill: COLORS.revenueRecovered },
    { name: 'Payments Collected', value: data.paymentsCollected.total, fill: COLORS.paymentsCollected },
    { name: 'Revenue Protected', value: data.revenueProtected.total, fill: COLORS.revenueProtected },
    { name: 'Staff Time Saved', value: data.staffTimeSaved.totalDollars, fill: COLORS.staffTimeSaved },
  ];

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>ROI Dashboard</h1>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {[1, 3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 500,
                background: months === m ? '#059669' : '#fff',
                color: months === m ? '#fff' : '#374151',
              }}>
              {m}mo
            </button>
          ))}
        </div>
      </div>

      {/* Hero ROI Card */}
      <div style={{
        background: 'linear-gradient(135deg, #059669, #047857)', borderRadius: '12px',
        padding: '2rem', marginBottom: '1.5rem', color: '#fff',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: '3.5rem', fontWeight: 800, lineHeight: 1 }}>
            {isFinite(roiMultiplier) && roiMultiplier > 0 ? `${roiMultiplier}x` : '—'}
          </div>
          <div style={{ fontSize: '1rem', opacity: 0.9, marginTop: '0.25rem' }}>Return on Investment</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Total Value Delivered</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{fmt(data.totalValue)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Monthly Subscription</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '2px' }}>
              <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>$</span>
              <input type="number" value={subscriptionCost} onChange={e => setSubscriptionCost(Number(e.target.value) || 0)}
                style={{
                  width: '80px', fontSize: '1.25rem', fontWeight: 700, background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)', borderRadius: '4px', color: '#fff',
                  textAlign: 'center', padding: '2px 4px',
                }} />
            </div>
          </div>
        </div>
      </div>

      {/* 4 Category Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {/* Revenue Recovered */}
        <CategoryCard title="Revenue Recovered" total={data.revenueRecovered.total} color={COLORS.revenueRecovered} icon="↗">
          {data.revenueRecovered.byCampaignType.length > 0 ? (
            data.revenueRecovered.byCampaignType.map(ct => (
              <LineItem key={ct.campaignType} label={ct.campaignType} value={fmt(ct.recovered)}
                sub={`${ct.completed}/${ct.enrollments} converted`} />
            ))
          ) : (
            <div style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>No campaign data in this period</div>
          )}
        </CategoryCard>

        {/* Payments Collected */}
        <CategoryCard title="Payments Collected" total={data.paymentsCollected.total} color={COLORS.paymentsCollected} icon="$">
          <LineItem label="Direct Payments" value={fmt(data.paymentsCollected.directNet)} sub={`${data.paymentsCollected.paymentCount} transactions`} />
          <LineItem label="Campaign Payments" value={fmt(data.paymentsCollected.campaignPayments)} sub={`${data.paymentsCollected.campaignPaymentCount} conversions`} />
          <LineItem label="Gateway Fees" value={`-${fmt(data.paymentsCollected.fees)}`} sub="" color="#DC2626" />
        </CategoryCard>

        {/* Revenue Protected */}
        <CategoryCard title="Revenue Protected" total={data.revenueProtected.total} color={COLORS.revenueProtected} icon="🛡">
          <LineItem label="No-Show Interventions"
            value={fmt(data.revenueProtected.noShow.value)}
            sub={`${data.revenueProtected.noShow.highRiskCount} high-risk × 40% × $${data.revenueProtected.noShow.avgRevenuePerVisit}`} />
          <LineItem label="Churn Prevention"
            value={fmt(data.revenueProtected.churn.value)}
            sub={`${data.revenueProtected.churn.highRiskCount} at-risk × 30% × $${data.revenueProtected.churn.avgPatientLTV}`} />
          <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#9CA3AF', fontStyle: 'italic' }}>
            AI-estimated based on risk scoring models
          </div>
        </CategoryCard>

        {/* Staff Time Saved */}
        <CategoryCard title="Staff Time Saved"
          total={data.staffTimeSaved.totalDollars}
          color={COLORS.staffTimeSaved} icon="⏱"
          subtitle={`${data.staffTimeSaved.totalHours} hours saved`}>
          <LineItem label="Voice AI Calls" value={fmtHrs(data.staffTimeSaved.voice.minutesSaved)}
            sub={`${data.staffTimeSaved.voice.count} calls (${fmt(data.staffTimeSaved.voice.dollarsSaved)})`} />
          <LineItem label="Automated Messages" value={fmtHrs(data.staffTimeSaved.messages.minutesSaved)}
            sub={`${data.staffTimeSaved.messages.count} messages (${fmt(data.staffTimeSaved.messages.dollarsSaved)})`} />
          <LineItem label="Digital Intake" value={fmtHrs(data.staffTimeSaved.intake.minutesSaved)}
            sub={`${data.staffTimeSaved.intake.count} forms (${fmt(data.staffTimeSaved.intake.dollarsSaved)})`} />
          <LineItem label="Ambient Scribe" value={fmtHrs(data.staffTimeSaved.scribe.minutesSaved)}
            sub={`${data.staffTimeSaved.scribe.count} sessions (${fmt(data.staffTimeSaved.scribe.dollarsSaved)})`} />
          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#6B7280' }}>
            <span>Staff rate:</span>
            <span>$</span>
            <input type="number" value={staffHourlyRate} onChange={e => setStaffHourlyRate(Number(e.target.value) || 0)}
              style={{
                width: '50px', fontSize: '0.75rem', border: '1px solid #D1D5DB',
                borderRadius: '4px', padding: '2px 4px', textAlign: 'center',
              }} />
            <span>/hr</span>
          </div>
        </CategoryCard>
      </div>

      {/* Value Breakdown Chart */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>Value Breakdown</h3>
        {data.totalValue > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {breakdownData.map(item => {
              const maxVal = Math.max(...breakdownData.map(d => d.value), 1);
              const pct = (item.value / maxVal) * 100;
              return (
                <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '140px', fontSize: '0.8rem', color: '#374151', textAlign: 'right' }}>{item.name}</div>
                  <div style={{ flex: 1, height: '24px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: item.fill, borderRadius: '4px', transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ width: '80px', fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>{fmt(item.value)}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>No value data to display yet</p>
        )}
      </div>

      {/* Monthly Trend */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>Monthly Trend</h3>
        {data.monthlyTrend && data.monthlyTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.monthlyTrend} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 12 }} />
              <Tooltip formatter={v => fmt(v)} />
              <Legend />
              <Area type="monotone" dataKey="revenueRecovered" name="Revenue Recovered"
                stackId="1" fill={COLORS.revenueRecovered} stroke={COLORS.revenueRecovered} fillOpacity={0.6} />
              <Area type="monotone" dataKey="paymentsCollected" name="Payments Collected"
                stackId="1" fill={COLORS.paymentsCollected} stroke={COLORS.paymentsCollected} fillOpacity={0.6} />
              <Area type="monotone" dataKey="staffTimeSaved" name="Staff Time Saved"
                stackId="1" fill={COLORS.staffTimeSaved} stroke={COLORS.staffTimeSaved} fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>No trend data available</p>
        )}
      </div>
    </div>
  );
}

/* ─── Helper Components ─── */

function CategoryCard({ title, total, color, icon, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>{icon} {title}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{fmt(total)}</div>
          {subtitle && <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: '0.75rem' }}>
        {children}
      </div>
    </div>
  );
}

function LineItem({ label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.35rem' }}>
      <div>
        <div style={{ fontSize: '0.8rem', color: '#374151' }}>{label}</div>
        {sub && <div style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{sub}</div>}
      </div>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: color || '#111827', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

function fmt(val) {
  const n = parseFloat(val) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtHrs(minutes) {
  const m = parseInt(minutes) || 0;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

export default ROIDashboard;
